import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { AlertTriangle, Loader2, ShieldAlert, CheckCircle2, X } from "lucide-react";
import { toast } from "sonner";
import api from "../services/api";

export default function DeleteAccountDialog({ open, onOpenChange }) {
  const [password, setPassword] = useState("");
  const [reason, setReason] = useState("");
  const [confirmText, setConfirmText] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [pending, setPending] = useState(null);
  const [loadingStatus, setLoadingStatus] = useState(false);

  const fetchStatus = async () => {
    setLoadingStatus(true);
    try {
      const { data } = await api.get("/account/delete-request");
      setPending(data?.request || null);
    } catch {
      setPending(null);
    } finally {
      setLoadingStatus(false);
    }
  };

  useEffect(() => {
    if (open) {
      setPassword(""); setReason(""); setConfirmText("");
      fetchStatus();
    }
  }, [open]);

  const submit = async (e) => {
    e?.preventDefault();
    if (!password) return toast.error("Enter your current password to confirm");
    if (confirmText.trim().toUpperCase() !== "DELETE") return toast.error('Type "DELETE" to confirm');
    setSubmitting(true);
    try {
      await api.post("/account/delete-request", { password, reason: reason || null });
      toast.success("Account scheduled for deletion. You can cancel any time within 30 days.");
      await fetchStatus();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't submit request");
    } finally { setSubmitting(false); }
  };

  const cancel = async () => {
    setSubmitting(true);
    try {
      await api.delete("/account/delete-request");
      toast.success("Deletion request cancelled");
      setPending(null);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't cancel");
    } finally { setSubmitting(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="delete-account-dialog">
        <DialogHeader>
          <DialogTitle className="font-['Outfit'] text-lg flex items-center gap-2 text-red-600">
            <ShieldAlert className="h-5 w-5" /> Delete my account
          </DialogTitle>
          <DialogDescription className="text-xs text-slate-500">
            We'll keep your data for 30 days so you can reverse this. After that, your account, orders, and personal data are permanently deleted (subject to legal retention for tax/audit records — see Privacy Policy).
          </DialogDescription>
        </DialogHeader>

        {loadingStatus ? (
          <div className="py-8 flex items-center justify-center text-slate-400 text-sm">
            <Loader2 className="h-4 w-4 animate-spin mr-2" /> Checking status…
          </div>
        ) : pending ? (
          <div className="space-y-4" data-testid="deletion-pending-card">
            <div className="rounded-xl border border-amber-200 bg-amber-50 p-4 space-y-2">
              <div className="flex items-center gap-2 text-amber-800">
                <AlertTriangle className="h-4 w-4" />
                <span className="text-sm font-semibold">Deletion scheduled</span>
              </div>
              <p className="text-xs text-amber-900 leading-relaxed">
                Your account is scheduled for permanent deletion on{" "}
                <b data-testid="scheduled-for">
                  {new Date(pending.scheduled_for).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })}
                </b>.
                Cancel any time before that to keep your account.
              </p>
              {pending.reason ? (
                <p className="text-xs text-amber-900/80"><b>Your reason:</b> {pending.reason}</p>
              ) : null}
            </div>
            <Button
              onClick={cancel}
              disabled={submitting}
              className="w-full bg-emerald-600 hover:bg-emerald-700 rounded-xl h-11"
              data-testid="cancel-deletion-btn"
            >
              {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <><CheckCircle2 className="h-4 w-4 mr-2" /> Keep my account</>}
            </Button>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-3">
            <div className="rounded-xl border border-red-100 bg-red-50/60 p-3 text-xs text-red-800 leading-relaxed flex gap-2">
              <AlertTriangle className="h-4 w-4 flex-shrink-0 mt-0.5" />
              <span>This will queue your account for deletion in 30 days. Active orders and wallet balance will be settled first.</span>
            </div>

            <div>
              <Label className="text-xs font-semibold text-slate-700">Reason <span className="text-slate-400">(optional, helps us improve)</span></Label>
              <Textarea
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                rows={2}
                maxLength={500}
                placeholder="e.g. switching to another service, no longer need it…"
                className="mt-1 rounded-xl text-sm"
                data-testid="delete-reason-input"
              />
            </div>

            <div>
              <Label className="text-xs font-semibold text-slate-700">Confirm with your password</Label>
              <Input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="mt-1 rounded-xl h-10"
                autoComplete="current-password"
                data-testid="delete-password-input"
              />
            </div>

            <div>
              <Label className="text-xs font-semibold text-slate-700">Type <span className="font-mono text-red-600">DELETE</span> to confirm</Label>
              <Input
                value={confirmText}
                onChange={(e) => setConfirmText(e.target.value)}
                className="mt-1 rounded-xl h-10 font-mono"
                placeholder="DELETE"
                data-testid="delete-confirm-input"
              />
            </div>

            <DialogFooter className="gap-2 sm:gap-2 pt-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
                className="rounded-xl h-11 flex-1"
                data-testid="delete-account-back-btn"
              >
                <X className="h-4 w-4 mr-1" /> Back
              </Button>
              <Button
                type="submit"
                disabled={submitting || confirmText.trim().toUpperCase() !== "DELETE" || !password}
                className="bg-red-600 hover:bg-red-700 rounded-xl h-11 flex-1 disabled:opacity-50"
                data-testid="submit-delete-account-btn"
              >
                {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : "Schedule deletion"}
              </Button>
            </DialogFooter>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
